import { useNetwork } from '@vueuse/core';

const SLOW_TYPES = new Set(['slow-2g', '2g']);
const OFFLINE_DEBOUNCE_MS = 2000;
const PROBE_TIMEOUT_MS = 5000;

let instance: ReturnType<typeof createConnectivity> | null = null;

function createConnectivity() {
  const { isOnline: rawIsOnline, effectiveType } = useNetwork();

  const isOnline = ref(rawIsOnline.value);
  let offlineTimer: ReturnType<typeof setTimeout> | null = null;

  watch(rawIsOnline, (online) => {
    if (online) {
      if (offlineTimer) {
        clearTimeout(offlineTimer);
        offlineTimer = null;
      }
      isOnline.value = true;
    } else {
      offlineTimer = setTimeout(() => {
        isOnline.value = false;
      }, OFFLINE_DEBOUNCE_MS);
    }
  });

  const isSlowConnection = computed(
    () => isOnline.value && !!effectiveType.value && SLOW_TYPES.has(effectiveType.value),
  );

  const connectionQuality = computed(() => {
    if (!isOnline.value) return 'offline';
    if (isSlowConnection.value) return 'slow';
    return 'good';
  });

  /**
   * Actively probe connectivity by hitting a tiny static asset. `navigator.onLine`
   * lies on some platforms (always-true when on a Wi-Fi network with no internet),
   * so the only reliable signal is an actual request.
   * Returns true if reachable; updates isOnline on either outcome.
   */
  async function checkConnectivity(): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    try {
      // Cache-bust query param so the probe always hits the network.
      await fetch(`/favicon.ico?_probe=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (offlineTimer) {
        clearTimeout(offlineTimer);
        offlineTimer = null;
      }
      isOnline.value = true;
      return true;
    } catch {
      isOnline.value = false;
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  return { isOnline, isSlowConnection, connectionQuality, effectiveType, checkConnectivity };
}

export function useConnectivity() {
  if (!instance) {
    instance = createConnectivity();
  }
  return instance;
}
